import {
  SearchEngine,
  loadFacetSetActions,
  loadSearchActions,
  loadSearchAnalyticsActions,
  FacetValueState,
} from "@coveo/headless";

export interface FacetSelection {
  field: string;
  values: string[];
}

const SELECTED: FacetValueState = "selected";

/**
 * Apply a deterministic facet preselect against a cached Coveo SearchEngine.
 *
 * For each requested field this:
 *  1. Deselects any existing values on that facet — guarantees the next
 *     dispatch either toggles an idle value to selected, or inserts the
 *     selection payload as-is (already marked selected), regardless of state
 *     left on the module-cached engine by a previous catalog page.
 *  2. Dispatches toggleSelectFacetValue per value. The reducer (headless 2.80,
 *     facet-set-slice) toggles when the value exists in currentValues and
 *     inserts as-is when it doesn't, so passing `state: 'selected'` covers
 *     both branches.
 *
 * Exactly one search request is dispatched at the end (via executeSearch with
 * a logFacetSelect analytics descriptor), so all preselected fields land in a
 * single /rest/search/v2 call.
 *
 * The facetId mirrors the field name to match the default chosen by
 * `<atomic-facet>`, avoiding duplicate registration in the engine's facetSet.
 */
export const applyFacetPreselect = (
  engine: SearchEngine,
  selections: FacetSelection[],
): void => {
  const facetSetActions = loadFacetSetActions(engine);
  const searchActions = loadSearchActions(engine);
  const analyticsActions = loadSearchAnalyticsActions(engine);

  let lastAppliedSelection: { facetId: string; facetValue: string } | null =
    null;

  for (const { field, values } of selections) {
    const cleanValues = values.map((v) => v.trim()).filter((v) => v.length > 0);
    if (cleanValues.length === 0) continue;

    engine.dispatch(facetSetActions.deselectAllFacetValues(field));

    for (const value of cleanValues) {
      engine.dispatch(
        facetSetActions.toggleSelectFacetValue({
          facetId: field,
          selection: { value, state: SELECTED, numberOfResults: 0 },
        }),
      );
      lastAppliedSelection = { facetId: field, facetValue: value };
    }
  }

  if (!lastAppliedSelection) return;

  engine.dispatch(
    searchActions.executeSearch(
      analyticsActions.logFacetSelect(lastAppliedSelection),
    ),
  );
};
